/**
 * Image prefetch: load key images in the background after first visit
 * so Photos, Films, Packages, About, Book, Budget Calculator load faster.
 */

const PREFETCH_IMAGES = [
  // Cover / Home hero (remaining after photo1 preload)
  '/albums/hero/photo2.webp',
  '/albums/hero/photo3.webp',
  '/albums/hero/photo12.webp',
  '/albums/hero/photo13.webp',
  '/albums/hero/photo32.webp',
  '/albums/hero/photo33.webp',
  // Photos page – first 3 images per album (visible in sliders)
  '/albums/portfoliophotos/ushing-makhyai/photo1.webp',
  '/albums/portfoliophotos/ushing-makhyai/photo2.webp',
  '/albums/portfoliophotos/ushing-makhyai/photo3.webp',
  '/albums/portfoliophotos/mongting-umeeya/photo1.webp',
  '/albums/portfoliophotos/mongting-umeeya/photo2.webp',
  '/albums/portfoliophotos/param-fullara/photo1.webp',
  '/albums/portfoliophotos/param-fullara/photo2.webp',
  '/albums/portfoliophotos/hlahlawang-maysingaung/photo1.webp',
  '/albums/portfoliophotos/prayas-nova/photo1.webp',
  '/albums/portfoliophotos/sujan-priya/photo1.webp',
  '/albums/portfoliophotos/ukhingmong-nyomay/photo1.webp',
  // Packages page cards
  '/albums/packages/1.webp',
  '/albums/packages/2.webp',
  '/albums/packages/3.webp',
  '/albums/packages/4.webp',
  '/albums/packages/5.webp',
  '/albums/packages/6.webp',
  '/albums/packages/11.webp',
  '/albums/packages/12.webp',
  '/albums/packages/13.webp',
  // Budget calculator hero
  '/albums/cover/budgetCalculatorHero.webp',
  // Footer logo (often used in SEO/social)
  '/albums/logo/logo.png',
];

const preloadOne = (src) => {
  return new Promise((resolve) => {
    if (!src || typeof src !== 'string') {
      resolve();
      return;
    }
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = src;
  });
};

/**
 * Prefetch images in the background after the given delay.
 * Uses requestIdleCallback when available so it doesn't compete with initial load.
 * @param {number} delayMs - Wait this long after call before starting (default 2500)
 * @param {number} staggerMs - Pause between each image (default 80)
 */
export const prefetchImages = (delayMs = 2500, staggerMs = 80) => {
  const run = () => {
    const list = [...PREFETCH_IMAGES];
    let index = 0;

    const next = () => {
      if (index >= list.length) return;
      const src = list[index++];
      const base = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
      const url = src.startsWith('http') ? src : base + src;
      preloadOne(url).then(() => {
        if (staggerMs <= 0) {
          while (index < list.length) {
            const s = list[index++];
            const u = s.startsWith('http') ? s : base + s;
            preloadOne(u);
          }
          return;
        }
        setTimeout(next, staggerMs);
      });
    };

    next();
  };

  if (typeof window === 'undefined') return;

  const startAfterLoad = () => {
    // Wait for delay so we don't compete with initial paint/hero load
    setTimeout(() => {
      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(run, { timeout: 5000 });
      } else {
        run();
      }
    }, delayMs);
  };

  if (document.readyState === 'complete') {
    startAfterLoad();
  } else {
    window.addEventListener('load', startAfterLoad);
  }
};

export { PREFETCH_IMAGES };
